import type { CreatePricePluginProps } from '@domains/subscription/priv/Billables/usecases/CreatePriceForm'
import { RangeLabel } from '@domains/subscription/priv/Billables/usecases/Price/tiered/components/RangeLabel'
import type { TieredPrice } from '@domains/subscription/priv/Billables/usecases/Price/tiered/tiered'
import { isTiered } from '@domains/subscription/priv/Billables/usecases/Price/tiered/tieredMapper'
import type { BaseAlgorithm, BasePrice } from '@domains/subscription/priv/components/Confirmable/confirmable'
import type { PaymentFrequencyRequest } from '@domains/subscription/priv/types'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { useFieldArray, useForm } from 'react-hook-form'

export interface TieredPriceFormProps extends CreatePricePluginProps {
  getAlgorithmPlugin: (algorithm: BaseAlgorithm) => React.ComponentType<CreatePricePluginProps> | undefined
}

export const TieredPriceForm = ({ price, getAlgorithmPlugin, onCreatedPrice, billableId, ...props }: TieredPriceFormProps) => {
  const theme = useTheme()

  const form = useForm({ values: defaultTieredPrice({ price, billableId: billableId }) })

  const defaultPaymentFrequency: PaymentFrequencyRequest = price?.paymentFrequency ?? { type: 'anchorable', period: 'monthly' }

  const tiers = useFieldArray({ control: form.control, name: 'tiers' })

  const handleAddTier = () => {
    const prevTier = tiers.fields[tiers.fields.length - 1]

    if (prevTier) {
      return tiers.append({
        range: { fromExclusive: prevTier.range.fromExclusive, toInclusive: undefined },
        algorithm: { type: prevTier.algorithm.type },
      })
    }

    tiers.append({
      range: { fromExclusive: 0, toInclusive: undefined },
      algorithm: { type: 'Linear' },
    })
  }

  const availablePlugins = props.additionalPlugins?.map((plugin) => plugin.type) ?? []

  return (
    <Stack
      sx={{
        gap: 1,
      }}
    >
      <Typography variant="h3">Tiers</Typography>
      <form onSubmit={form.handleSubmit((data) => onCreatedPrice(data))}>
        <Stack
          sx={{
            gap: 2,
          }}
        >
          <Stack>
            {tiers.fields.map((tier, index) => {
              const AlgorithmComponent = getAlgorithmPlugin(tier.algorithm)
              const tierId = `tier-${index + 1}`

              return (
                <Accordion key={tierId}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`${tierId}-content`} id={`${tierId}-header`}>
                    <RangeLabel from={tier.range.fromExclusive} to={tier.range.toInclusive} unit={props.unit} />
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: 0 }}>
                    <Stack
                      sx={{
                        gap: 2,
                      }}
                    >
                      <Paper sx={{ backgroundColor: theme.palette.grey[100], p: 2 }}>
                        <Stack
                          sx={{
                            gap: 3,
                          }}
                        >
                          <Stack
                            sx={{
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Typography variant="p2Medium">Tier range settings</Typography>
                            <FormControl sx={{ minWidth: 100 }}>
                              <InputLabel>Price type</InputLabel>
                              <Select
                                size="small"
                                label="Price type"
                                value={tier.algorithm.type}
                                onChange={(event) => {
                                  tiers.update(index, {
                                    ...tier,
                                    algorithm: { type: event.target.value },
                                  })
                                }}
                              >
                                {availablePlugins.map((pluginType) => (
                                  <MenuItem key={pluginType} value={pluginType}>
                                    {pluginType}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Stack>
                          <Stack
                            sx={{
                              gap: 2,
                              flexDirection: 'row',
                            }}
                          >
                            <TextField
                              label="From (exclusive)"
                              fullWidth
                              error={!!form.formState.errors?.tiers?.[index]?.range?.fromExclusive}
                              {...form.register(`tiers.${index}.range.fromExclusive`, { required: true, valueAsNumber: true })}
                            />
                            <TextField
                              label="To (inclusive)"
                              fullWidth
                              {...form.register(`tiers.${index}.range.toInclusive`, {
                                setValueAs: (value) => (value === '' ? undefined : Number.isNaN(Number(value)) ? undefined : Number(value)),
                              })}
                            />
                          </Stack>
                        </Stack>
                      </Paper>
                      <Paper sx={{ backgroundColor: theme.palette.grey[100], p: 2 }}>
                        <Stack
                          sx={{
                            gap: 3,
                          }}
                        >
                          <Typography variant="p2Medium">Price settings</Typography>
                          {AlgorithmComponent ? (
                            <AlgorithmComponent
                              price={{ ...tier.algorithm, paymentFrequency: defaultPaymentFrequency }}
                              billableId={billableId}
                              {...props}
                              onPriceChange={(data) => {
                                console.log('tier', index, 'data', data)
                                form.setValue(`tiers.${index}.algorithm`, data)
                              }}
                              onCreatedPrice={noop}
                            />
                          ) : (
                            <Typography data-testid="missing-plugin-error" variant="p4Regular" sx={{ color: 'red' }}>
                              Backoffice does not support '{tier.algorithm.type}' price types in this configuration
                            </Typography>
                          )}
                        </Stack>
                      </Paper>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )
            })}
          </Stack>
          <Stack
            sx={{
              alignItems: 'flex-end',
            }}
          >
            <Button onClick={handleAddTier} variant="outlined" size="small">
              Add tier +
            </Button>
          </Stack>
          <Button type="submit" variant="contained">
            Save Tiers
          </Button>
        </Stack>
      </form>
    </Stack>
  )
}

interface DefaultTieredPriceParams {
  price: BasePrice | undefined
  billableId: string
}

const defaultTieredPrice = ({ price, billableId }: DefaultTieredPriceParams): TieredPrice<BaseAlgorithm> => {
  if (isTiered(price)) return price

  return {
    type: 'Tiered',
    tiers: [],
    billableId,
    paymentFrequency: price?.paymentFrequency ?? { type: 'anchorable', period: 'monthly' },
    supported: true,
  }
}

const noop = () => {}
